/**
 * Registers a global Axios response interceptor that silently swallows
 * network errors when the browser is offline.
 *
 * IMPORTANT: This must be imported once at app startup (index.tsx) before
 * any React components mount, so the interceptor is active before any
 * network requests are made.
 */
import axios from 'axios';

import { apiUrl } from '@/helpers';

const OFFLINE_EVENT_COOLDOWN_MS = 10000;
const OFFLINE_PROBE_TIMEOUT_MS = 2500;

let lastOfflineEventDispatchedAt = 0;
let activeOfflineProbe: Promise<boolean> | null = null;

const dispatchOfflineEventWithCooldown = () => {
  const now = Date.now();

  if (now - lastOfflineEventDispatchedAt < OFFLINE_EVENT_COOLDOWN_MS) {
    return;
  }

  lastOfflineEventDispatchedAt = now;
  window.dispatchEvent(new Event('offline'));
};

const probeApiReachability = async (): Promise<boolean> => {
  if (!navigator.onLine) {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), OFFLINE_PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`${apiUrl()}/health`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    });

    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const confirmApiIsOffline = async (): Promise<boolean> => {
  if (!activeOfflineProbe) {
    activeOfflineProbe = probeApiReachability().finally(() => {
      activeOfflineProbe = null;
    });
  }

  return activeOfflineProbe;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isNetworkError =
      error.code === 'ERR_NETWORK' ||
      error.code === 'ERR_NAME_NOT_RESOLVED' ||
      (!error.response && error.message === 'Network Error');

    if (isNetworkError) {
      const isApiReachable = await confirmApiIsOffline();

      if (!isApiReachable) {
        // Dispatch offline event so useOnlineStatus hook updates all components.
        dispatchOfflineEventWithCooldown();
        // Return empty resolved response to prevent react-query error logging.
        return Promise.resolve({ data: null, status: 0, headers: {} });
      }
    }

    return Promise.reject(error);
  }
);
